import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, firstValueFrom, Observable } from "rxjs";
import { environment } from "src/environments/environment";
import { Role } from "../models/enums/roles";
import { GridState } from "../models/grid-state";
import { JwtPayload } from "../models/JWT";
import { User } from "../models/user";
import { JwtService } from "../services/jwt.service";
import { MessagesService } from "../services/messages.service";

@Injectable({
    providedIn: 'root'
})
export class UserService {
    private _baseUrl = environment.api;
    private _currentUser?: User;
    private _logoutTimeoutId?: number;

    public readonly $isAdmin = new BehaviorSubject<boolean>(false);
    public readonly $isLoggedIn = new BehaviorSubject<boolean>(false);
    public readonly $avatarUrl = new BehaviorSubject<string>('');
    public readonly $user = new BehaviorSubject<User | undefined>(undefined);

    public get currentUser(): User | undefined {
        return this._currentUser;
    }
    public get initials(): string | undefined {
        if (!this.currentUser) {
            return '';
        }
        let initials = this.currentUser.firstName.substring(0, 1);
        initials += this.currentUser.lastName.substring(0, 1);
        return initials;
    }
    private async kickOutExpired() {
        if (this.jwtService.jwt) this.msgs.info('Your session has expired.');
        this.jwtService.set();
        this.logout();
    }
    public async updateCurrentUser() {
        clearTimeout(this._logoutTimeoutId);
        if (this.jwtService.jwt === undefined) this.logout();
        else {
            if (this._currentUser && this._currentUser.id !== this.jwtService.jwt.id) {
                this._currentUser.id = this.jwtService.jwt.id;
            }
            try {
                this._currentUser = await firstValueFrom(this.http.get<User>(`${this._baseUrl}/users/${this.jwtService.jwt.id}`));
                this._currentUser = this.setRoleToView(this._currentUser);
            }
            catch (err) {
                return this.logout();
            }

            this.$user.next(this._currentUser);
            this.$isAdmin.next(this._currentUser?.role == Role.admin);
            this.$isLoggedIn.next(true);
            this.$avatarUrl.next(`${this._baseUrl}/${this._currentUser?.avatar || '/media/avatars/default.png'}`);

            if (this.jwtService.jwt.exp * 1000 < Date.now()) {
                this.kickOutExpired();
                return;
            }
            this._logoutTimeoutId = window.setTimeout(this.kickOutExpired.bind(this), this.jwtService.jwt.exp * 1000 - Date.now());
        }
    }
    public async login(username: string, password: string): Promise<JwtPayload> {
        await firstValueFrom(this.http.post(`${this._baseUrl}/users/login`, { username, password }));
        this.updateCurrentUser();
        return this.jwtService.jwt!;
    }
    public logout(): void {
        this.jwtService.set();
        this._currentUser = undefined;

        this.$user.next(this._currentUser);
        this.$isAdmin.next(false);
        this.$isLoggedIn.next(false);
        this.$avatarUrl.next(`${this._baseUrl}/media/avatars/default.png?ts=${Date.now()}`);
    }
    public async all(): Promise<User[]> {
        let users = await firstValueFrom(this.http.get<User[]>(`${this._baseUrl}/users`));
        users = users.map(u => this.setRoleToView(u));
        return users;
    }
    public async add(user: User): Promise<User> {
        const data = this.setRoleToDB(user);
        const result = await firstValueFrom(this.http.post<User>(`${this._baseUrl}/users/add`, data));
        return this.setRoleToView(result);
    }
    public async update(user: User): Promise<User> {
        const data = this.setRoleToDB(user);
        const result = await firstValueFrom(this.http.put<User>(`${this._baseUrl}/users/update`, data));
        return this.setRoleToView(result);
    }
    public async addAvatar(formData: FormData): Promise<string> {
        await firstValueFrom(this.http.post<string>(`${this._baseUrl}/users/avatar`, formData));
        await this.updateCurrentUser();
        return this.currentUser?.avatar!;
    }
    public updatePassword = (data: any): Observable<any> => {
        return this.http.post<any>(`${this._baseUrl}/users/update-password`, data);
    }
    public renewPassword = (id: string | undefined): Observable<any> => {
        return this.http.post<any>(`${this._baseUrl}/users/renew-password`, { id });
    }
    public gridStates(): Observable<GridState[]> {
        return this.http.get<GridState[]>(`${this._baseUrl}/users/grid-states`);
    }
    public async updateGridState(state: GridState): Promise<any> {
        return await firstValueFrom(this.http.post<any>(`${this._baseUrl}/users/update-grid-state`, { id: state.id, userId: state.userId, options: state.options, gridName: state.gridName }));
    }
    constructor(
        private http: HttpClient,
        private msgs: MessagesService,
        private jwtService: JwtService
    ) {
        // TODO: kick out on session expired
        this.updateCurrentUser().catch(err => msgs.error(err));
    }

    private setRoleToView =(input: User): User => {
        input.role = Role.user;
        if ((input as any).isStaff) input.role = Role.moderator;
        if ((input as any).isSuperuser) input.role = Role.admin;
        return input;
    }

    private setRoleToDB =(input: User): any => {
        const result: any = input;
        result.isSuperuser = input.role === Role.admin;
        result.isStaff = input.role === Role.moderator;
        return result;
    }
}
